import type { DimensionUnit, Item } from '@easymoving/types';

/** Metres per one unit (SI base for volume math). */
const UNIT_TO_M: Record<DimensionUnit, number> = {
  cm: 0.01,
  m: 1,
  in: 0.0254,
  ft: 0.3048,
};

const KG_PER_LB = 0.45359237;

export const kgToLb = (kg: number): number => kg / KG_PER_LB;
export const lbToKg = (lb: number): number => lb * KG_PER_LB;

export const M3_TO_FT3 = 35.3146667;

/**
 * Volume of a single item in cubic metres, or null if it lacks complete
 * structured dimensions. Quantity is NOT applied here — callers multiply.
 */
export function itemVolumeM3(item: Item): number | null {
  const { length, width, height, dimensionUnit } = item;
  if (!length || !width || !height || !dimensionUnit) return null;
  const f = UNIT_TO_M[dimensionUnit];
  return length * f * (width * f) * (height * f);
}

export interface MeasureTotals {
  /** Total weight in kilograms (stored unit). */
  weightKg: number;
  /** Total volume in cubic metres. */
  volumeM3: number;
  /** How many items contributed a weight / a volume (× their quantity). */
  weightItems: number;
  volumeItems: number;
}

/**
 * Above this a packed box risks backs and mover surcharges — movers commonly
 * cap single-person boxes around 20kg. Used for the "split this box" warning.
 */
export const HEAVY_BOX_KG = 20;

/** Total known weight (kg) of the items assigned to one box. Pure. */
export function boxWeightKg(items: Item[], boxId: string): number {
  let kg = 0;
  for (const it of items) {
    if (it.boxId === boxId && it.weight != null) kg += it.weight * (it.quantity ?? 1);
  }
  return kg;
}

/**
 * Sum weight and volume across items, each scaled by its quantity. Items
 * missing the relevant data are skipped (and not counted). Pure.
 */
export function computeMeasureTotals(items: Item[]): MeasureTotals {
  let weightKg = 0;
  let volumeM3 = 0;
  let weightItems = 0;
  let volumeItems = 0;
  for (const it of items) {
    const qty = it.quantity ?? 1;
    if (it.weight != null) {
      weightKg += it.weight * qty;
      weightItems += qty;
    }
    const v = itemVolumeM3(it);
    if (v != null) {
      volumeM3 += v * qty;
      volumeItems += qty;
    }
  }
  return { weightKg, volumeM3, weightItems, volumeItems };
}
